import { motion, AnimatePresence } from 'framer-motion'

export interface ContextMenuItem {
  label: string
  icon: React.ReactNode
  onClick: () => void
  destructive?: boolean
}

interface ContextMenuProps {
  open: boolean
  position: { x: number; y: number }
  items: ContextMenuItem[]
  onClose: () => void
}

export default function ContextMenu({ open, position, items, onClose }: ContextMenuProps) {
  // Clamp menu position so it doesn't overflow viewport
  const menuWidth = 200
  const menuHeight = items.length * 48 + 16 // approximate
  const x = Math.min(position.x, window.innerWidth - menuWidth - 16)
  const y = Math.min(position.y, window.innerHeight - menuHeight - 16)

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50" onClick={onClose}>
          <div className="absolute inset-0" />
          <motion.div
            className="absolute bg-elevated rounded-xl shadow-xl overflow-hidden py-2"
            style={{ left: x, top: y, minWidth: menuWidth }}
            initial={{ opacity: 0, scale: 0.85, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: -8 }}
            transition={{ type: 'spring', damping: 25, stiffness: 400, duration: 0.15 }}
            onClick={(e) => e.stopPropagation()}
          >
            {items.map((item) => (
              <button
                key={item.label}
                onClick={() => {
                  item.onClick()
                  onClose()
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                  item.destructive
                    ? 'text-error hover:bg-error/10'
                    : 'text-text-primary hover:bg-surface'
                }`}
              >
                <span className="w-5 h-5 shrink-0">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
